<template>
  <div>
    <section class="analytics__form">
      <form class="form-content">
        <rum-select
          class="options__nowrap"
          v-if="userProjects.length > 1"
          :class="{ 'select-preview': selectedProjects.length > 4 }"
          :style="selectedProjectsLength"
          v-model="selectedProjects"
          placeholder="Select projects"
          :options="userProjects"
          label="name"
          clearable
          multiple
          :closeOnSelect="false"
          :clearSearchOnSelect="false"
          :filterBy="filterBy"
        >
          <template slot="option" slot-scope="option">
            <div class="project-option">
              <div class="project-option__header">
                {{ option.name }}
                <span
                  class="project-option__organization"
                >Organization: {{option.organization}}</span>
              </div>
              <i class="fas fa-plus add-option"></i>
              <i class="fas fa-times remove-option" @click="removeThisOption(option.id)"></i>
            </div>
          </template>
        </rum-select>
        <div
          class="form-content__date-range"
          :style="[userProjects.length > 1 ? { 'padding-top': 'var(--spacing-4)' } : {} ]"
        >
          <rum-select
            class="date-range__field"
            v-model="selectedDateRange"
            placeholder="Select date range"
            :options="dateRanges"
            @input="calculateGroupings"
            :clearable="false"
          />
          <rum-select
            class="date-range__field grouping-field"
            v-model="selectedGrouping"
            placeholder="Select grouping"
            :options="groupings"
            :clearable="false"
          />
        </div>
        <div
          v-if="selectedDateRange == 'Select Date Range'"
          class="form-content__date-range"
          style="padding-top: var(--spacing-4);"
        >
          <rum-datepicker
            class="date-range__field"
            v-model="selectedFrom"
            placeholder="From"
            limitToDate
            :limitYears="2"
          />
          <rum-datepicker
            class="date-range__field"
            v-model="selectedTo"
            placeholder="To"
            limitToDate
            :limitYears="2"
          />
        </div>
        <rum-button v-if="userProjects" class="action__show" type="button" @click="submitInput">Show</rum-button>
        <rum-loader full v-if="!userProjects" />
      </form>
    </section>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "RumieAnalyticsForm",
  props: {
    userProjects: Array,
    individual: Boolean,
    previousInput: Object
  },
  data() {
    return {
      selectedProjects: "",
      selectedDateRange: "Last 30 Days",
      selectedGrouping: "Day",
      selectedFrom: "",
      selectedTo: "",
      dateRanges: [
        "Today",
        "Yesterday",
        "This Week",
        "This Month",
        "This Quarter",
        "This Year",
        "Last 7 Days",
        "Last 30 Days",
        "Last Week",
        "Last Month",
        "Last Quarter",
        "Last Year",
        "Select Date Range"
      ],
      groupings: ["Day", "Week"],
      validationErrors: []
    };
  },
  computed: {
    selectedProjectsLength() {
      return `--selected-project-length: "${this.selectedProjects.length} Projects Selected"`;
    }
  },
  methods: {
    setSelectedProjects() {
      // Defaults selectedProjects to first 1-3 projects if user has access to mere than 3.
      this.selectedProjects =
        this.userProjects.length > 3
          ? this.userProjects.slice(0, 3)
          : this.userProjects;
      if (!this.individual) {
        this.submitInput();
      }
    },
    validateDateRange() {
      // Validates custom date range and creates error message for notofocation on validation error.
      if (moment(this.selectedTo).diff(moment(this.selectedFrom), "days") > 0) {
        return true;
      } else {
        if (
          !this.validationErrors.filter(error => error.type == "date").length
        ) {
          this.validationErrors.push({
            type: "date",
            message: "Please select valid date range!"
          });
        }
        return false;
      }
    },
    /**
     * Method: calculateGroupings()
     * Based on selectedDateRange or (chosen custom date range) calculates optimal groupings.
     * Defaults selectedDateRange to the last element of groupings array when calculated.
     * For custom date range requires both selectedFrom and selectedTo to be set.
     */

    calculateGroupings() {
      if (this.selectedDateRange != "Select Date Range") {
        switch (this.selectedDateRange) {
          // Hour
          case "Today":
            this.groupings = ["Hour"];
            break;
          case "Yesterday":
            this.groupings = ["Hour"];
            break;
          // Day
          case "This Week":
            this.groupings = ["Day"];
            break;
          case "Last 7 Days":
            this.groupings = ["Day"];
            break;
          case "Last Week":
            this.groupings = ["Day"];
            break;
          // Day, Week
          case "This Month":
            this.groupings = ["Day", "Week"];
            break;
          case "Last 30 Days":
            this.groupings = ["Day", "Week"];
            break;
          case "Last Month":
            this.groupings = ["Day", "Week"];
            break;
          // Day, Week, Month
          case "This Quarter":
            this.groupings = ["Day", "Week", "Month"];
            break;
          case "Last Quarter":
            this.groupings = ["Day", "Week", "Month"];
            break;
          case "This Year":
            this.groupings = ["Day", "Week", "Month"];
            break;
          case "Last Year":
            this.groupings = ["Day", "Week", "Month"];
            break;
        }
        this.selectedGrouping =
          this.individual &&
          this.selectedDateRange === this.previousInput.selectedDateRange
            ? this.selectedGrouping
            : this.groupings[this.groupings.length - 1];
      } else {
        if (this.selectedFrom && this.selectedTo) {
          const diff = moment(this.selectedTo).diff(
            moment(this.selectedFrom),
            "days"
          );
          if (!this.validateDateRange()) {
            this.$notify({
              type: "error",
              title: this.validationErrors
                .filter(error => error.type == "date")
                .map(error => error.message)
                .toString()
            });
            this.selectedGrouping = "";
            this.groupings = [];
          } else {
            switch (true) {
              // Hour
              case diff == 1:
                this.groupings = ["Hour"];
                break;
              // Day
              case diff > 1 && diff <= 14:
                this.groupings = ["Day"];
                break;
              // Day, Week
              case diff > 14 && diff <= 30:
                this.groupings = ["Day", "Week"];
                break;
              // Day, Week, Month
              case diff > 30 && diff <= 366:
                this.groupings = ["Day", "Week", "Month"];
                break;
              // Day, Week, Month, Year
              case diff > 366:
                this.groupings = ["Day", "Week", "Month", "Year"];
                break;
            }
            this.selectedGrouping =
              this.individual &&
              this.selectedDateRange === this.previousInput.selectedDateRange
                ? this.selectedGrouping
                : this.groupings[this.groupings.length - 1];
          }
        } else {
          this.selectedGrouping = "";
          this.groupings = [];
        }
      }
    },
    /**
     * Method: inputIsValid()
     * Validates user unput. On validation errors pushes error messages to validationErrors.
     */

    inputIsValid() {
      let inputIsValid = false;

      if (
        this.selectedDateRange != "Select Date Range" ||
        this.validateDateRange()
      ) {
        inputIsValid = true;
        if (
          this.validationErrors.filter(error => error.type == "date").length
        ) {
          this.validationErrors.splice(
            this.validationErrors.findIndex(project => project.type == "date"),
            1
          );
        }
      } else {
        if (
          !this.validationErrors.filter(error => error.type == "date").length
        ) {
          this.validationErrors.push({
            type: "date",
            message: "Please select valid date range!"
          });
        }
      }

      if (!this.selectedProjects.length) {
        if (
          !this.validationErrors.filter(error => error.type == "projects")
            .length
        ) {
          this.validationErrors.push({
            type: "projects",
            message: "Please select at least one project!"
          });
        }
        inputIsValid = false;
      } else {
        if (
          this.validationErrors.filter(error => error.type == "projects").length
        ) {
          this.validationErrors.splice(
            this.validationErrors.findIndex(
              project => project.type == "projects"
            ),
            1
          );
        }
      }

      return inputIsValid;
    },
    /**
     * Method: submitInput()
     * If user input is valid, emits userInput object to parent. If not, displays validation errors as notofocations.
     *
     * * There are three query measures user has control over:
     * * - selectedProjects:
     * User project ids come from database as integers.
     * ! NOTE: CubeJS does not accept array of integers, it has to be array of strings.
     * Solution: make all members of an array strings.
     * E.g.: ["1", "2"].
     * * - selectedDateRange:
     * If user have chosen a relative date range (e.g. "Last 30 Days" by default)
     * selectedDateRange is a string and it's just fine.
     * However, if user have chosen custom date range, then submit
     * an array [selectedFrom, selectedTo].
     * ! NOTE: If it's a  custom date range, it also has to be array of strings (same as selectedProjects).
     * Solution: make all members of an array strings.
     * E.g.: ["2015-01-01", "2015-12-31"].
     * * - selectedGrouping:
     * Comes as a string, that starts from capital letter.
     * ! NOTE: CubeJS only takes it as a all lowecase string.
     * Solution: toLowerCase().
     * E.g.: "day"
     */

    submitInput() {
      if (this.inputIsValid()) {
        let userInput = {};
        userInput["selectedProjects"] = this.selectedProjects.map(
          project => `${project.id}`
        );
        if (this.selectedDateRange != "Select Date Range") {
          userInput["selectedDateRange"] = this.selectedDateRange;
        } else {
          userInput["selectedDateRange"] = [
            moment(this.selectedFrom).format("YYYY-MM-DD"),
            moment(this.selectedTo).format("YYYY-MM-DD")
          ].map(date => `${date}`);
        }
        userInput["selectedGrouping"] = this.selectedGrouping.toLowerCase();
        this.$emit("formReport", userInput);
      } else {
        this.$notify({
          type: "error",
          title: this.validationErrors
            .map(error => error.message)
            .join(" ")
            .toString()
        });
      }
    },
    restorePreviousInput() {
      this.selectedProjects = this.userProjects;
      this.selectedDateRange = this.previousInput.selectedDateRange;
      this.selectedGrouping =
        this.previousInput.selectedGrouping.charAt(0).toUpperCase() +
        this.previousInput.selectedGrouping.slice(1);
    },
    removeThisOption(id) {
      this.selectedProjects = this.selectedProjects.filter(
        project => project.id != id
      );
    },
    filterBy(option, label, search) {
      return (
        label.toLowerCase().indexOf(search.toLowerCase()) > -1 ||
        option.organization.toLowerCase().indexOf(search.toLowerCase()) > -1
      );
    }
  },
  watch: {
    userProjects: function() {
      this.setSelectedProjects();
    },
    selectedDateRange: function() {
      this.calculateGroupings();
    },
    selectedFrom: function() {
      this.calculateGroupings();
    },
    selectedTo: function() {
      this.calculateGroupings();
    }
  },
  mounted() {
    if (this.userProjects.length) {
      this.setSelectedProjects();
    }
    if (this.individual) {
      this.restorePreviousInput();
    }
  }
};
</script>

<style scoped>
.analytics__form {
  display: flex;
  flex-direction: column;
  color: var(--grey);
  background-color: var(--white);
}

.form-content {
  padding: var(--spacing-4);
}

.project-option {
  padding: var(--spacing-1);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.project-option__header {
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 400px;
  display: block;
  overflow: hidden;
}

.project-option__organization {
  display: block;
  font-size: var(--xs);
}

.form-content__date-range {
  display: flex;
  flex-direction: row;
}

.date-range__field {
  width: 100%;
}

.form-content__date-range .date-range__field + .date-range__field {
  margin-left: var(--spacing-4);
}

.action__show {
  margin-top: var(--spacing-4);
  padding: var(--spacing-4);
  width: 100%;
}

@media screen and (max-width: 650px) {
  .form-content__date-range {
    flex-direction: column;
  }

  .date-range__field {
    margin-top: var(--spacing-4);
  }

  .form-content__date-range .date-range__field + .date-range__field {
    margin-left: 0;
  }
}

>>> .grouping-field .selected-tag::before {
  content: "By\00a0";
}

>>> .grouping-field .dropdown-menu li a::before {
  content: "By ";
}

>>> .options__nowrap .vs__selected-options .selected-tag {
  white-space: nowrap;
}

>>> .select-preview .vs__selected {
  display: none;
}

>>> .select-preview .vs__selected-options::before {
  content: var(--selected-project-length);
  height: max-content;
  align-self: center;
  margin-left: 7px;
}

>>> .vs__dropdown-menu {
  overflow-x: hidden;
}

>>> .vs__dropdown-menu li .add-option {
  display: block;
}

>>> .vs__dropdown-menu li .remove-option {
  display: none;
}

>>> .vs__dropdown-menu .vs__dropdown-option--selected {
  background-color: var(--background);
}

>>> .vs__dropdown-menu .vs__dropdown-option--selected:hover {
  color: inherit;
}

>>> .vs__dropdown-menu .vs__dropdown-option--selected .add-option {
  display: none;
}

>>> .vs__dropdown-menu .vs__dropdown-option--selected .remove-option {
  display: block;
}
</style>
